import React from 'react';
import { useTranslation } from 'react-i18next';
import { Loading } from '@carbon/react';
import { Exit, Money, User, UserFollow, WarningAlt } from '@carbon/react/icons';
import BucketedBarChart from 'src/components/Dashboard/BucketedBarChart';
import CategoryBarChart from 'src/components/Dashboard/CategoryBarChart';
import StatCard from 'src/components/Dashboard/StatCard';
import { useDashboardStaffReports } from 'src/hooks/useDashboardReports';
import { useMoney } from 'src/hooks/useMoney';
import { useNameLookups } from './useNameLookups';

interface StaffProps {}

/**
 * Headcount, payroll and the invite funnel are SNAPSHOTS — current roster, whatever the date
 * range says — so they carry "as of today". New hires, days off and fines follow the range.
 */
const Staff = React.memo((_: StaffProps) => {
  const { t } = useTranslation('dashboard');
  const { decimals, format, formatCompact } = useMoney();
  const {
    query: { isLoading, data: report, isRefetching },
    bucket,
  } = useDashboardStaffReports();
  const { employeeName, branchName } = useNameLookups();
  const payroll = report?.payroll;
  const asOfToday = `(${t('asOfToday')})`;
  // Hourly staff have a rate, not a cost: the figure is rate × the restaurant's configured
  // hours, so the tile names that assumption — and names none until the response carries it.
  const hoursAssumption =
    payroll?.payrollHoursPerMonth != null ? ` — ${t('hourlyAt', { hours: payroll.payrollHoursPerMonth })}` : '';

  return (
    <div className={'staff'}>
      {(isLoading || isRefetching) && <Loading small />}
      <div className="stats">
        <StatCard
          title={`${t('activeStaff')} ${asOfToday}`}
          value={report?.headcount?.active ?? 0}
          icon={<User size={36} />}
        />
        <StatCard
          title={`${t('deactivatedStaff')} ${asOfToday}`}
          value={report?.headcount?.deactivated ?? 0}
          icon={<Exit size={36} />}
        />
        {/* Invited but not yet onboarded (no linked account) — the ones worth chasing. */}
        <StatCard
          title={`${t('pendingInvites')} ${asOfToday}`}
          value={report?.inviteFunnel?.pending ?? 0}
          icon={<UserFollow size={36} />}
        />
      </div>
      <div className="stats">
        {/* Salaried staff only — hourly staff are the estimated tile beside it. */}
        <StatCard
          title={`${t('salariedPayroll')} ${asOfToday}`}
          value={payroll?.salaried ?? 0}
          format={formatCompact}
          decimals={decimals}
          icon={<Money size={36} />}
        />
        <StatCard
          title={`${t('estimatedPayroll')} ${asOfToday}${hoursAssumption}`}
          value={payroll?.hourlyEstimated ?? 0}
          format={formatCompact}
          decimals={decimals}
          icon={<Money size={36} />}
        />
        {/* Salaried + hourly (est.) combined — carries the same hours assumption as the
            estimated tile, since it folds that estimate in. */}
        <StatCard
          title={`${t('totalPayroll')} ${asOfToday}${hoursAssumption}`}
          value={payroll?.total ?? 0}
          format={formatCompact}
          decimals={decimals}
          icon={<Money size={36} />}
        />
      </div>
      <div className="stats">
        <StatCard title={t('newHires')} value={report?.newHires ?? 0} icon={<UserFollow size={36} />} />
        <StatCard title={t('daysOff')} value={report?.dayOff?.count ?? 0} icon={<WarningAlt size={36} />} />
        <StatCard
          title={t('dayOffAmount')}
          value={report?.dayOff?.amount ?? 0}
          format={formatCompact}
          decimals={decimals}
          icon={<Money size={36} />}
        />
      </div>
      <div className="stats">
        <BucketedBarChart
          title={t('daysOffPerBucket')}
          dateFormat={bucket === 'month' ? 'MM/YYYY' : 'DD/MM'}
          data={(report?.dayOffByBucket || []).map((row) => ({
            bucketKey: row.bucketKey,
            group: t('daysOff'),
            value: row.count,
          }))}
        />
      </div>
      <div className="stats">
        <CategoryBarChart
          title={t('daysOffByBranch')}
          categoryAxisTitle={t('branch')}
          valueAxisTitle={t('count')}
          data={(report?.dayOffByBranch || []).map((row) => ({ label: branchName(row.branchId), value: row.count }))}
        />
      </div>
      <div className="stats">
        <CategoryBarChart
          title={`${t('staffingMix')} ${asOfToday}`}
          valueAxisTitle={t('count')}
          data={[
            { label: t('managers'), value: report?.headcount?.managers ?? 0 },
            { label: t('employees'), value: report?.headcount?.employees ?? 0 },
            { label: t('fullTime'), value: report?.headcount?.fullTime ?? 0 },
            { label: t('partTime'), value: report?.headcount?.partTime ?? 0 },
          ]}
        />
      </div>
      <div className="stats">
        {/* Includes the hourly estimate, so it carries the same hours assumption as the
            estimated- and total-payroll tiles. */}
        <CategoryBarChart
          title={`${t('payrollBreakdown')} ${asOfToday}${hoursAssumption}`}
          valueAxisTitle={t('payroll')}
          data={[
            { label: t('salaried'), value: payroll?.salaried ?? 0 },
            { label: t('hourlyEstimated'), value: payroll?.hourlyEstimated ?? 0 },
          ]}
          axisValueFormatter={formatCompact}
          tooltipValueFormatter={format}
        />
      </div>
      <div className="stats">
        <CategoryBarChart
          title={`${t('headcountByBranch')} ${asOfToday}`}
          categoryAxisTitle={t('branch')}
          valueAxisTitle={t('count')}
          data={(report?.headcountByBranch || []).map((row) => ({ label: branchName(row.branchId), value: row.count }))}
        />
      </div>
      <div className="stats">
        <CategoryBarChart
          title={t('topAbsentees')}
          categoryAxisTitle={t('employee')}
          valueAxisTitle={t('count')}
          data={(report?.topAbsentees || []).map((row) => ({ label: employeeName(row.employeeId), value: row.count }))}
        />
      </div>
      <div className="stats">
        <CategoryBarChart
          title={t('finesByEmployee')}
          categoryAxisTitle={t('employee')}
          valueAxisTitle={t('fine')}
          data={(report?.finesByEmployee || []).map((row) => ({
            label: employeeName(row.employeeId),
            value: row.fine,
          }))}
          axisValueFormatter={formatCompact}
          tooltipValueFormatter={format}
        />
      </div>
    </div>
  );
});

export default Staff;
